import {type SyntheticEvent, useCallback, useRef, useState} from 'react'
import {View} from 'react-native'
import ReactPlayer from 'react-player'

import {atoms as a} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import type {VideoPlayerProps} from './types'
import {VideoPlayerControls} from './VideoPlayerControls'

export function VideoPlayer({
  videoId,
  orientation,
  onEnded,
  onError,
}: VideoPlayerProps) {
  const playerRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Player state
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [buffering, setBuffering] = useState(true)
  const [muted, setMuted] = useState(false)
  const [ready, setReady] = useState(false)

  // Hover state for showing controls
  const {
    state: hovered,
    onIn: onHover,
    onOut: onEndHover,
  } = useInteractionState()

  // Hide controls timeout
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [controlsVisible, setControlsVisible] = useState(true)

  const showControls = useCallback(() => {
    setControlsVisible(true)
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }
    hideTimeoutRef.current = setTimeout(() => {
      if (playing) {
        setControlsVisible(false)
      }
    }, 3000)
  }, [playing])

  const handleMouseMove = useCallback(() => {
    showControls()
  }, [showControls])

  // Player callbacks using standard HTML video events
  const handleReady = useCallback(() => {
    setReady(true)
    setBuffering(false)
  }, [])

  const handlePlay = useCallback(
    (_event: SyntheticEvent<HTMLVideoElement>) => {
      setPlaying(true)
      showControls()
    },
    [showControls],
  )

  const handlePause = useCallback(
    (_event: SyntheticEvent<HTMLVideoElement>) => {
      setPlaying(false)
      setControlsVisible(true)
    },
    [],
  )

  const handleTimeUpdate = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget
      setCurrentTime(video.currentTime)
    },
    [],
  )

  const handleDurationChange = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget
      setDuration(video.duration)
    },
    [],
  )

  const handleWaiting = useCallback(
    (_event: SyntheticEvent<HTMLVideoElement>) => {
      setBuffering(true)
    },
    [],
  )

  const handlePlaying = useCallback(
    (_event: SyntheticEvent<HTMLVideoElement>) => {
      setBuffering(false)
    },
    [],
  )

  const handleEnded = useCallback(
    (_event: SyntheticEvent<HTMLVideoElement>) => {
      setPlaying(false)
      onEnded?.()
    },
    [onEnded],
  )

  const handleError = useCallback(
    (_event: SyntheticEvent<HTMLVideoElement, Event>) => {
      onError?.(_event)
    },
    [onError],
  )

  // Control callbacks
  const handlePlayPause = useCallback(() => {
    setPlaying(prev => !prev)
    showControls()
  }, [showControls])

  const handleSeek = useCallback((time: number) => {
    if (playerRef.current) {
      playerRef.current.currentTime = time
    }
    setCurrentTime(time)
  }, [])

  const handleToggleMute = useCallback(() => {
    setMuted(prev => !prev)
  }, [])

  const handleToggleFullscreen = useCallback(() => {
    if (!containerRef.current) return

    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      void containerRef.current.requestFullscreen()
    }
  }, [])

  const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: orientation === 'vertical' ? 9 / 16 : 16 / 9,
        maxWidth: orientation === 'vertical' ? 350 : undefined,
        alignSelf: orientation === 'vertical' ? 'center' : undefined,
        backgroundColor: 'black',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={onHover}
      onMouseLeave={onEndHover}>
      <ReactPlayer
        ref={playerRef}
        src={youtubeUrl}
        playing={playing}
        muted={muted}
        width="100%"
        height="100%"
        controls={false}
        onReady={handleReady}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={handleTimeUpdate}
        onDurationChange={handleDurationChange}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onEnded={handleEnded}
        onError={handleError}
        config={{
          youtube: {
            rel: 0, // Don't show related videos from other channels
            iv_load_policy: 3, // Hide annotations
            disablekb: 1, // Disable keyboard controls (we handle our own)
            fs: 0, // Hide fullscreen button (we have our own)
            color: 'white', // White progress bar instead of red
          },
        }}
        // CSS to hide YouTube logo watermark and other UI elements
        style={{
          pointerEvents: playing ? 'none' : 'auto', // Prevent clicks on YouTube UI when playing
        }}
      />

      {/* Custom controls overlay */}
      {ready && (
        <VideoPlayerControls
          playing={playing}
          currentTime={currentTime}
          duration={duration}
          buffering={buffering}
          muted={muted}
          visible={controlsVisible || hovered || !playing}
          onPlayPause={handlePlayPause}
          onSeek={handleSeek}
          onToggleMute={handleToggleMute}
          onToggleFullscreen={handleToggleFullscreen}
          onInteractionStart={showControls}
          onInteractionEnd={showControls}
        />
      )}

      {/* Loading state before player is ready */}
      {!ready && (
        <View
          style={[a.absolute, a.inset_0, a.justify_center, a.align_center]}
          pointerEvents="none">
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              borderWidth: 3,
              borderColor: 'white',
              borderTopColor: 'transparent',
            }}
          />
        </View>
      )}
    </div>
  )
}
